import React, { useEffect, useState } from 'react';
import { supabase } from '../../services/supabase';

/**
 * AuthVerification Component
 * 
 * This component is shown when a user navigates directly to a protected route
 * (e.g., /diet-app) but the server doesn't find an auth cookie.
 * 
 * It checks client-side auth (localStorage) and:
 * 1. If authenticated: Sets the cookie and redirects to the intended destination
 * 2. If not authenticated: Redirects to login page
 * 
 * This prevents the bounce through login page for already-authenticated users.
 */
const AuthVerification = () => {
  const [status, setStatus] = useState('Checking authentication...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyAndRedirect = async () => {
      try {
        // Get return URL from query params
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = urlParams.get('return') || '/';
        
        console.log('[AUTH VERIFY] Checking auth for:', returnUrl);
        
        // Check for client-side session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[AUTH VERIFY] Session check error:', sessionError);
        }
        
        if (session?.access_token && session?.user) {
          // User is authenticated client-side
          console.log('[AUTH VERIFY] User authenticated, setting cookie and redirecting');
          setStatus('Authenticated! Redirecting...');
          
          // Set cookie for server-side auth check
          try {
            const expires = new Date(session.expires_at * 1000).toUTCString();
            const cookieValue = `sb-access-token=${session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
            document.cookie = cookieValue;
            console.log('[AUTH VERIFY] Cookie set successfully');
          } catch (cookieError) {
            console.error('[AUTH VERIFY] Error setting cookie:', cookieError);
            // Continue anyway - cookie might still work
          }
          
          // Small delay to ensure cookie is set, then redirect
          setTimeout(() => {
            console.log('[AUTH VERIFY] Redirecting to:', returnUrl);
            window.location.href = returnUrl;
          }, 100);
        } else {
          // User is not authenticated, redirect to login
          console.log('[AUTH VERIFY] User not authenticated, redirecting to login');
          setStatus('Not authenticated. Redirecting to login...');
          
          setTimeout(() => {
            const loginUrl = `/?page=login&return=${encodeURIComponent(returnUrl)}`;
            window.location.href = loginUrl;
          }, 500);
        }
      } catch (err) {
        console.error('[AUTH VERIFY] Error during verification:', err);
        setError(err.message || 'Authentication check failed');
        
        // On error, redirect to login after a delay
        setTimeout(() => {
          const urlParams = new URLSearchParams(window.location.search);
          const returnUrl = urlParams.get('return') || '/';
          const loginUrl = `/?page=login&return=${encodeURIComponent(returnUrl)}`;
          window.location.href = loginUrl;
        }, 2000);
      }
    };

    verifyAndRedirect();
  }, []);

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{status}</p>
        {error && (
          <p className="text-red-600 mt-2">{error}</p>
        )}
      </div>
    </div>
  );
};

export default AuthVerification;


